"use client";
import { CompositeRowContext, useCompositeScopedContext } from "./composite-context.js";
import { createElement, createHook, forwardRef, useId, useWrapElement } from "@ariakit/react-utils";
import { invariant, removeUndefinedValues } from "@ariakit/utils";
import { useMemo } from "react";
import { jsx } from "react/jsx-runtime";
import { useStoreState } from "@ariakit/react-store";
//#region src/composite/composite-row.tsx
const TagName = "div";
/**
* Returns props to create a `CompositeRow` component. Wrapping `CompositeItem`
* elements within rows will create a two-dimensional composite widget, such as
* a grid.
* @see https://ariakit.com/components/composite
* @example
* ```jsx
* const store = useCompositeStore();
* const props = useCompositeRow({ store });
* <Composite store={store}>
*   <Role {...props}>
*     <CompositeItem>Item 1</CompositeItem>
*     <CompositeItem>Item 2</CompositeItem>
*     <CompositeItem>Item 3</CompositeItem>
*   </Role>
* </Composite>
* ```
*/
const useCompositeRow = createHook(function useCompositeRow({ store, "aria-setsize": ariaSetSize, "aria-posinset": ariaPosInSet, ...props }) {
	const context = useCompositeScopedContext();
	store = store || context;
	invariant(store, process.env.NODE_ENV !== "production" && "CompositeRow must be wrapped in a Composite component.");
	const id = useId(props.id);
	const baseElement = useStoreState(store, (state) => state.baseElement || void 0);
	const providerValue = useMemo(() => ({
		id,
		baseElement,
		ariaSetSize,
		ariaPosInSet
	}), [
		id,
		baseElement,
		ariaSetSize,
		ariaPosInSet
	]);
	props = useWrapElement(props, (element) => /* @__PURE__ */ jsx(CompositeRowContext.Provider, {
		value: providerValue,
		children: element
	}), [providerValue]);
	props = {
		...props,
		id
	};
	return removeUndefinedValues(props);
});
/**
* Renders a row element for composite items that allows two-dimensional arrow
* key navigation.
* [`CompositeItem`](https://ariakit.com/reference/composite-item) elements
* wrapped within this component will automatically receive a
* [`rowId`](https://ariakit.com/reference/composite-item#rowid) prop.
* @see https://ariakit.com/components/composite
* @example
* ```jsx {3-12}
* <CompositeProvider>
*   <Composite>
*     <CompositeRow>
*       <CompositeItem>Item 1.1</CompositeItem>
*       <CompositeItem>Item 1.2</CompositeItem>
*       <CompositeItem>Item 1.3</CompositeItem>
*     </CompositeRow>
*     <CompositeRow>
*       <CompositeItem>Item 2.1</CompositeItem>
*       <CompositeItem>Item 2.2</CompositeItem>
*       <CompositeItem>Item 2.3</CompositeItem>
*     </CompositeRow>
*   </Composite>
* </CompositeProvider>
* ```
*/
const CompositeRow = forwardRef(function CompositeRow(props) {
	return createElement(TagName, useCompositeRow(props));
});
//#endregion
export { CompositeRow, useCompositeRow };

//# sourceMappingURL=composite-row.js.map